import { getRequiredFields } from "components/form/utils";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useMemo } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { ticketSchema } from "./schema";
import { fetchTicketDataForForm } from "./read";
import { useCreateTicket } from "../create/mutation";
import { useUpdateTicket } from "../update/mutation";
import { fileUpload } from "directus/src/operations";

export const useTicketForm = ({ defaultTicketId }) => {
  const createTicketMutation = useCreateTicket();
  const updateTicketMutation = useUpdateTicket();
  const navigate = useNavigate();

  const form = useForm({
    resolver: zodResolver(ticketSchema),
    defaultValues: {
      id: defaultTicketId || "",
      title: "",
      description: "",
      status: "open",
      attachments: [],
    },
  });

  const requiredFields = useMemo(
    () => getRequiredFields(ticketSchema),
    [ticketSchema]
  );

  const handleCreate = async (context) => {
    const values = context.values;

    // Upload new attachments first and get IDs
    let attachmentIds = [];
    if (Array.isArray(values.attachments) && values.attachments.length > 0) {
      const newFiles = values.attachments.filter((f) => f.isNew && f.file);
      const existingFiles = values.attachments.filter((f) => !f.isNew && f.id);

      // Upload new files
      const uploadedFiles = [];
      for (const fileObj of newFiles) {
        try {
          const formData = new FormData();
          formData.append("file", fileObj.file);
          const uploaded = await fileUpload(formData);
          uploadedFiles.push(uploaded.id);
        } catch (error) {
          console.error("File upload failed:", error);
          // Continue with other files even if one fails
        }
      }

      // Combine existing IDs with newly uploaded IDs
      attachmentIds = [...existingFiles.map((f) => f.id), ...uploadedFiles];
    }

    // Transform attachments to junction table format
    const transformedValues = {
      ...values,
      attachments: attachmentIds.map((id) => ({ attachment: id })),
    };

    const response = await createTicketMutation.mutateAsync({
      values: transformedValues,
    });

    navigate(`/support/my-tickets/${response.id}`);
  };

  async function handleRead(id) {
    const response = await fetchTicketDataForForm({ id });

    // Transform attachments from junction table format to file objects
    const attachments = response.attachments?.map((att) => {
      const attachment = att.attachment;
      return {
        id: attachment?.id,
        name: attachment?.filename_download || `File ${attachment?.id}`,
        type: attachment?.type || "",
        url: attachment?.id
          ? `${import.meta.env?.VITE_BASE_URL}/assets/${attachment.id}`
          : null,
        isImage: attachment?.type?.startsWith("image/") || false,
        isVideo: attachment?.type?.startsWith("video/") || false,
        isExisting: true,
      };
    }) || [];

    const values = {
      ...response,
      description: response.description || "",
      attachments,
    };
    return values;
  }

  async function handleUpdate(id, context) {
    const values = context.dirtyValues;

    // Upload new attachments if attachments field is dirty
    let attachmentIds = null;
    if (values.attachments !== undefined) {
      if (Array.isArray(values.attachments) && values.attachments.length > 0) {
        const newFiles = values.attachments.filter((f) => f.isNew && f.file);
        const existingFiles = values.attachments.filter((f) => !f.isNew && f.id);

        // Upload new files
        const uploadedFiles = [];
        for (const fileObj of newFiles) {
          try {
            const formData = new FormData();
            formData.append("file", fileObj.file);
            const uploaded = await fileUpload(formData);
            uploadedFiles.push(uploaded.id);
          } catch (error) {
            console.error("File upload failed:", error);
            // Continue with other files even if one fails
          }
        }

        // Combine existing IDs with newly uploaded IDs
        attachmentIds = [...existingFiles.map((f) => f.id), ...uploadedFiles];
      } else {
        attachmentIds = [];
      }
    }

    const transformedValues = {
      ...values,
      ...(attachmentIds !== null && {
        attachments: attachmentIds.map((id) => ({ attachment: id })),
      }),
    };

    await updateTicketMutation.mutateAsync({
      ticketId: id,
      values: transformedValues,
    });

    navigate(`/support/all-tickets/${id}`);
  }

  console.log(form.formState.errors);

  return {
    form,
    requiredFields,
    handleCreate,
    handleRead,
    handleUpdate,
  };
};
